import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { showLoader, hideLoader } from "./LoaderSlice";
import * as FileSaver from "file-saver";
import {
  getPartnerProductList,
  getRoyaltySalesRequestData,
  getRoyaltySalesMappedData,
  deleteSalesRequest,
  addSalesRequest,
  editSalesRequest,
  getSingleRoyaltySalesRequestData,
  downloadRoyaltyPOApprovalRequestsDocument,
} from "../../services";
import { showMessage } from "../../helpers";

// Create async thunks for API calls
export const getPartnersProductList = createAsyncThunk(
  "royaltySales/getPartnersProductList",
  async (_, { dispatch }) => {
    try {
      const response = await getPartnerProductList();
      return response || [];
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

export const getSalesRequestData = createAsyncThunk(
  "royaltySales/getSalesRequestData",
  async ({ isApproved, partner_id, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getRoyaltySalesRequestData(isApproved, partner_id);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess(response);
      }
      return response || [];
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const getSalesMappedData = createAsyncThunk(
  "royaltySales/getSalesMappedData",
  async (isApproved, { dispatch }) => {
    try {
      const response = await getRoyaltySalesMappedData(isApproved);
      return response || [];
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

export const getSingleSalesRequestData = createAsyncThunk(
  "royaltySales/getSingleSalesRequestData",
  async (id, { dispatch }) => {
    try {
      const response = await getSingleRoyaltySalesRequestData(id);
      return response || [];
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
);

export const deleteSalesRequestData = createAsyncThunk(
  "royaltySales/deleteSalesRequestData",
  async ({ id, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await deleteSalesRequest(id);
      dispatch(hideLoader());
      showMessage("success", "Removed!");
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const raiseSalesRequest = createAsyncThunk(
  "royaltySales/raiseSalesRequest",
  async ({ data, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await addSalesRequest(data);
      dispatch(hideLoader());
      showMessage("success", "Sales Request raised succcessfully!");
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      let errorMsg = "";
      try {
        if (error && error.error) {
          // Handle both array and non-array error formats
          if (Array.isArray(error.error)) {
            errorMsg = error.error.filter((value) => value !== "None");
          } else if (typeof error.error === 'string') {
            errorMsg = [error.error];
          } else if (typeof error.error === 'object') {
            errorMsg = [JSON.stringify(error.error)];
          }
        }
      } catch (e) {
        console.log("Error parsing error:", e);
      }
      if (errorMsg && errorMsg.length) {
        showMessage("error", "Error");
      }
      throw error;
    }
  },
);

export const editRoyaltySalesRequestData = createAsyncThunk(
  "royaltySales/editRoyaltySalesRequestData",
  async ({ id, data, onSuccess, status }, { dispatch }) => {
    dispatch(showLoader());
    try {
      await editSalesRequest(id, data);
      dispatch(hideLoader());
      if (status) {
        showMessage("success", status);
      } else {
        showMessage("success", "Request edited succcessfully!");
      }
      if (onSuccess) {
        onSuccess();
      }
      return true;
    } catch (error) {
      dispatch(hideLoader());
      let errorMsg = "";
      try {
        if (error && error.error) {
          // Handle both array and non-array error formats
          if (Array.isArray(error.error)) {
            errorMsg = error.error.filter((value) => value !== "None");
          } else if (typeof error.error === 'string') {
            errorMsg = [error.error];
          } else if (typeof error.error === 'object') {
            errorMsg = [JSON.stringify(error.error)];
          }
        }
      } catch (e) {
        console.log("Error parsing error:", e);
      }
      if (errorMsg && errorMsg.length) {
        showMessage("error", errorMsg[0]);
        showMessage("error", "Error");
      }
      throw error;
    }
  },
);

export const downloadPOApprovalRequestsDocument = createAsyncThunk(
  "royaltySales/downloadPOApprovalRequestsDocument",
  async ({ filename, requestObj, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response =
        await downloadRoyaltyPOApprovalRequestsDocument(requestObj);
      dispatch(hideLoader());
      FileSaver.saveAs(response, filename);
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

const initialState = {
  partnersProductList: [],
  salesrequestdata: [],
  salesmappeddata: [],
  editSalesRequestData: [],
  isSalesRequestEdited: false,
  isSalesRequestDeleted: false,
  isSalesRequestAdded: false,
  isRoyaltySalesLoaded: false,
  isRoyaltySalesMappedLoaded: false,
};

const royaltySalesSlice = createSlice({
  name: "royaltySales",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Partners Product List
      .addCase(getPartnersProductList.fulfilled, (state, action) => {
        state.partnersProductList = action.payload;
      })

      // Get Sales Request Data
      .addCase(getSalesRequestData.pending, (state) => {
        state.isRoyaltySalesLoaded = false;
      })
      .addCase(getSalesRequestData.fulfilled, (state, action) => {
        state.isRoyaltySalesLoaded = true;
        state.salesrequestdata = action.payload;
      })
      .addCase(getSalesRequestData.rejected, (state) => {
        state.isRoyaltySalesLoaded = false;
      })

      // Get Sales Mapped Data
      .addCase(getSalesMappedData.pending, (state) => {
        state.isRoyaltySalesMappedLoaded = false;
      })
      .addCase(getSalesMappedData.fulfilled, (state, action) => {
        state.isRoyaltySalesMappedLoaded = true;
        state.salesmappeddata = action.payload;
      })
      .addCase(getSalesMappedData.rejected, (state) => {
        state.isRoyaltySalesMappedLoaded = false;
      })

      // Single Sales Request Data
      .addCase(getSingleSalesRequestData.pending, (state) => {
        state.isSalesRequestAdded = false;
      })
      .addCase(getSingleSalesRequestData.fulfilled, (state, action) => {
        state.isSalesRequestAdded = true;
        state.editSalesRequestData = action.payload;
      })
      .addCase(getSingleSalesRequestData.rejected, (state) => {
        state.isSalesRequestAdded = false;
      })

      // Delete Sales Request
      .addCase(deleteSalesRequestData.pending, (state) => {
        state.isSalesRequestDeleted = false;
      })
      .addCase(deleteSalesRequestData.fulfilled, (state) => {
        state.isSalesRequestDeleted = true;
      })
      .addCase(deleteSalesRequestData.rejected, (state) => {
        state.isSalesRequestDeleted = false;
      })

      // Raise Sales Request
      .addCase(raiseSalesRequest.pending, (state) => {
        state.isSalesRequestAdded = false;
      })
      .addCase(raiseSalesRequest.fulfilled, (state) => {
        state.isSalesRequestAdded = true;
      })
      .addCase(raiseSalesRequest.rejected, (state) => {
        state.isSalesRequestAdded = false;
      })

      // Edit Sales Request
      .addCase(editRoyaltySalesRequestData.pending, (state) => {
        state.isSalesRequestEdited = false;
      })
      .addCase(editRoyaltySalesRequestData.fulfilled, (state) => {
        state.isSalesRequestEdited = true;
      })
      .addCase(editRoyaltySalesRequestData.rejected, (state) => {
        state.isSalesRequestEdited = false;
      })

      // --- ADD THESE CASES FOR CLASSIC ACTIONS ---
      .addCase("SINGLE_SALE_VALUE_SUCCESS", (state, action) => {
        state.editSalesRequestData = action.data;
      })
      .addCase("SINGLE_SALE_VALUE_FAILURE", (state) => {
        state.editSalesRequestData = [];
      });
  },
});

export default royaltySalesSlice.reducer;
